import { type ReactElement } from "react";
import { type PrimitiveAtom, useAtom } from "jotai";
import { t } from "@lingui/core/macro";

import { Icon } from "_/components/icon";

import { type PrintViewControls } from "_/state/viewer";

import * as S from "./styled";

type ToolVisibilityButtonProps = {
  tool: "T0" | "T1";
  visible: boolean;
  onClick: () => void;
};

const ToolVisibilityButton = ({
  tool,
  visible,
  onClick,
}: ToolVisibilityButtonProps): ReactElement => {
  const title = visible
    ? t`components.controls.analyze.hide-tool ${tool}`
    : t`components.controls.analyze.show-tool ${tool}`;

  return (
    <S.ToolbarButton
      type="button"
      size="tiny"
      kind={visible ? "primary" : "secondary"}
      title={title}
      aria-pressed={visible}
      onClick={onClick}
    >
      {tool}
    </S.ToolbarButton>
  );
};

type AnalyzeViewportToolbarProps = {
  controlsAtom: PrimitiveAtom<PrintViewControls>;
};

export const AnalyzeViewportToolbar = ({
  controlsAtom,
}: AnalyzeViewportToolbarProps): ReactElement => {
  const [controls, setControls] = useAtom(controlsAtom);

  return (
    <S.Toolbar
      as="div"
      role="toolbar"
      aria-label={t`components.controls.analyze.path-visibility`}
    >
      <ToolVisibilityButton
        tool="T0"
        visible={controls.showTool0}
        onClick={() => {
          setControls((current) => ({
            ...current,
            showTool0: !current.showTool0,
          }));
        }}
      />

      <ToolVisibilityButton
        tool="T1"
        visible={controls.showTool1}
        onClick={() => {
          setControls((current) => ({
            ...current,
            showTool1: !current.showTool1,
          }));
        }}
      />

      <S.ToolbarButton
        type="button"
        size="tiny"
        kind={controls.showNonExtrusionMotion ? "primary" : "secondary"}
        iconOnly
        title={t`components.controls.analyze.show-motion`}
        aria-pressed={controls.showNonExtrusionMotion}
        onClick={() => {
          setControls((current) => {
            const showNonExtrusionMotion = !current.showNonExtrusionMotion;
            return {
              ...current,
              showNonExtrusionMotion,
              zRange: {
                ...current.zRange,
                value: showNonExtrusionMotion
                  ? current.zRange.range
                  : current.zRange.extrudedRange,
              },
            };
          });
        }}
      >
        <Icon variant="ArrowCircleUp" />
      </S.ToolbarButton>
    </S.Toolbar>
  );
};
